/**
 * Generic event queue that handles queueing and dispatching of custom events
 * when a component is not ready to receive them
 */
export class WindowEventQueue<T> {
  private pendingCalls: T[] = [];
  private isReady: boolean = false;
  private readonly eventName: string;

  constructor(eventName: string) {
    this.eventName = eventName;
  }

  /**
   * Call this AFTER event listeners are registered
   */
  setReady(ready: boolean): void {
    this.isReady = ready;

    // Process any pending calls when becoming ready
    if (ready && this.pendingCalls.length > 0) {
      // Process the most recent call (in case there are multiple)
      const latestCall = this.pendingCalls[this.pendingCalls.length - 1];
      this.pendingCalls = []; // Clear the queue
      // Dispatch the event immediately since we're ready
      this.dispatchEvent(latestCall);
    }
  }

  /**
   * Clear pending calls when unmounted
   */
  clearPendingCalls(): void {
    this.isReady = false;
    this.pendingCalls = [];
  }

  /**
   * Add an event call to the queue if Component is not ready, or dispatch immediately if ready
   */
  queueOrDispatch(payload: T): void {
    if (this.isReady) {
      this.dispatchEvent(payload);
    } else {
      this.pendingCalls.push(payload);
    }
  }

  isEventReady(): boolean {
    return this.isReady;
  }

  getPendingCallsCount(): number {
    return this.pendingCalls.length;
  }

  private dispatchEvent(payload: T): void {
    window.dispatchEvent(
      new CustomEvent(this.eventName, {
        detail: payload,
      }),
    );
  }
}
